// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The one place power-flow direction is decided.
 *
 * Formerly `kgGroupEdges.ts`, which also rolled unit-level relationships up
 * into group-to-group edges for the type-grouped Asset Topology. Power Flow
 * draws asset→asset edges, so the roll-up is gone; direction normalisation and
 * the unit-level adjacency it produced are what remain, and they are the two
 * pieces every consumer actually depended on.
 *
 * Getting the direction backwards silently reverses the whole diagram, which is
 * why it lives in exactly one tested module rather than being re-derived per
 * consumer. `powerFlowGraph.ts`, `equipmentFlowGraph.ts` and `assetLineup.ts`
 * all read it from here.
 */

import type { Relationship } from '../types/digitalTwin';

/**
 * Power-flow relationship types, keyed by the direction they are stored in.
 *
 * Taken from `definitions.Relationship.oneOf` in the ontology schema, using
 * each type's `x-ontology.inverse`. Only power flow counts here:
 *
 * - `connectedTo`/`connectedFrom` are excluded — the schema's own `$comment`
 *   calls them "general, non-power-flow connections, such as data or
 *   communication links", so they would draw comms topology over a power chain.
 * - `primarySource`/`backupSource` are *inverse* directions: the schema
 *   describes them as the source of power **for a device**, so the
 *   relationship's `source` is the consumer and its `target` is the supplier.
 *   Getting this backwards silently reverses the chain, so it is asserted in
 *   the tests. Neither declares an `x-ontology.inverse`, which is why they are
 *   handled by hand here — pinned by a test so a schema change that gives them
 *   one is noticed.
 * - `containsEquipment`/`hasLocation` are containment, not flow. Including them
 *   would make the site node feed everything at once.
 * - `feeds`/`fedBy`/`isFedBy` are **fluid**, not power. GA-11 (MOCK-GA10), the
 *   reference payload, publishes every conductor as `suppliesPowerTo` /
 *   `suppliedBy` and uses `feeds`/`isFedBy` only for air and chilled water
 *   (`Air_Header feeds CRAH`, `Chilled_Water_Pump feeds Evaporator`). Drawing
 *   them would put the cooling loop on the one-line. See `FLUID_FLOW_TYPES`.
 */
const FORWARD_FLOW_TYPES: ReadonlyMap<string, string> = new Map([
  ['suppliesPowerTo', 'suppliesPowerTo'],
]);

/** Stored source→target is the reverse of power flow; flip and relabel. */
const INVERSE_FLOW_TYPES: ReadonlyMap<string, string> = new Map([
  ['suppliedBy', 'suppliesPowerTo'],
  ['primarySource', 'suppliesPowerTo'],
  ['backupSource', 'suppliesPowerTo'],
]);

/**
 * Air and water paths: Brick's `feeds` and its inverses. Not drawn on the
 * one-line; counted so the notice can say what was left to Floor Plan.
 */
const FLUID_FLOW_TYPES: ReadonlySet<string> = new Set(['feeds', 'fedBy', 'isFedBy']);

/** True when this relationship type is an air or water path rather than a conductor. */
export const isFluidRelationshipType = (type: string): boolean => FLUID_FLOW_TYPES.has(type);

/** True when this relationship type contributes to the power-flow chain. */
export const isFlowRelationshipType = (type: string): boolean =>
  FORWARD_FLOW_TYPES.has(type) || INVERSE_FLOW_TYPES.has(type);

/** A flow relationship with its direction normalised to point downstream. */
export interface NormalizedFlowEdge {
  source: string;
  target: string;
  /** Canonical forward type name, e.g. `suppliedBy` → `suppliesPowerTo`. */
  relType: string;
}

/**
 * Reduce a relationship to a downstream-pointing edge, or `null` if it is not
 * a power-flow relationship.
 *
 * A namespaced predicate (`brick:suppliesPowerTo`) returns `null`
 * here, which would drop a conductor with no error. That is why every consumer
 * is expected to count unrecognised predicates —
 * `unknownVocabulary` in `ontologyAccess.ts` does it, and
 * `powerFlowGraph.ts` surfaces the count in the UI.
 */
export const normalizeFlowRelationship = (rel: Relationship): NormalizedFlowEdge | null => {
  const forward = FORWARD_FLOW_TYPES.get(rel.type);
  if (forward) return { source: rel.source, target: rel.target, relType: forward };

  const inverse = INVERSE_FLOW_TYPES.get(rel.type);
  if (inverse) return { source: rel.target, target: rel.source, relType: inverse };

  return null;
};

/** An asset's flow neighbours, both directions, deduped and sorted. */
export interface FlowAdjacency {
  /** Entity ids that feed this asset. */
  in: string[];
  /** Entity ids this asset feeds. */
  out: string[];
}

/**
 * Every asset's upstream and downstream flow neighbours.
 *
 * Deliberately unfiltered by what is on the diagram: the detail panel's
 * "Fed by / Feeds" lists are the accessible per-asset view of the same data
 * (design §9) and must not lose a neighbour because the canvas excluded it.
 * `powerFlowGraph.ts` applies the canvas filter separately.
 *
 * Sorted because the details endpoint is cursor-paginated, so page order is not
 * stable and an unsorted list would reorder between polls.
 */
export const buildFlowAdjacency = (
  relationships: readonly Relationship[]
): Map<string, FlowAdjacency> => {
  const adjacency = new Map<string, FlowAdjacency>();

  const entryFor = (id: string): FlowAdjacency => {
    let entry = adjacency.get(id);
    if (!entry) {
      entry = { in: [], out: [] };
      adjacency.set(id, entry);
    }
    return entry;
  };

  for (const rel of relationships) {
    const flow = normalizeFlowRelationship(rel);
    if (!flow) continue;
    entryFor(flow.source).out.push(flow.target);
    entryFor(flow.target).in.push(flow.source);
  }

  for (const entry of Array.from(adjacency.values())) {
    entry.in = dedupeSorted(entry.in);
    entry.out = dedupeSorted(entry.out);
  }

  return adjacency;
};

const dedupeSorted = (ids: string[]): string[] => Array.from(new Set(ids)).sort();
